// Premium medication-page sections — bright, glass-morphic, conversion-first.
// Used by /medications/:slug after the hero.  Renders only when data exists.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ChevronRight, Sparkles, Syringe, TrendingUp } from "lucide-react";

const RED = "#E11D2A";

/* ────────────────────────────  SECTION — WHAT IS IT  ──────────────────────────── */
export function WhatIsIt({ med }) {
  if (!med?.what) return null;
  return (
    <section
      className="relative bg-white py-16 md:py-20 border-y border-black/5"
      data-testid="med-what-section"
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Section 1</p>
          <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-4xl sm:text-5xl lg:text-6xl">
            What Is<br /><span style={{ color: RED }}>{med.name}?</span>
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[16px] md:text-[18px] text-black/75 leading-relaxed">{med.what}</p>
          {med.bullets?.length > 0 && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {med.bullets.map((b) => (
                <div key={b} className="px-4 py-3 rounded-xl border bg-white/70 backdrop-blur"
                  style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                  <p className="text-[12.5px] font-extrabold tracking-tight text-black">{b}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────  SECTION — WHY PATIENTS CHOOSE  ──────────────────────────── */
export function WhyPatientsChoose({ med }) {
  if (!med?.benefits?.length) return null;
  return (
    <section
      className="relative bg-white py-16 md:py-20"
      data-testid="med-benefits-section"
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
        <header className="max-w-[760px] mb-10">
          <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Section 2</p>
          <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-4xl sm:text-5xl lg:text-6xl">
            Why Patients Choose<br /><span style={{ color: RED }}>{med.name}.</span>
          </h2>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {med.benefits.map((b, idx) => (
            <article key={b.title}
              data-testid={`med-benefit-${idx}`}
              className="group relative rounded-2xl overflow-hidden bg-white p-6 transition-all duration-500 hover:-translate-y-1"
              style={{
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 20px 60px -30px rgba(0,0,0,0.20), 0 1px 0 rgba(255,255,255,0.6) inset",
              }}
            >
              <div aria-hidden className="absolute -top-px left-6 right-6 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: `linear-gradient(90deg, transparent, ${RED}, transparent)` }} />
              <span className="font-heavy text-[14px] tracking-[0.18em]" style={{ color: RED }}>
                {String(idx + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-3 font-heavy tracking-tight text-black text-[19px] leading-snug">{b.title}</h3>
              <p className="mt-2.5 text-[13px] text-black/65 leading-relaxed">{b.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────  SECTION — EXPECTED RESULTS  ──────────────────────────── */
export function ExpectedResults({ med }) {
  if (!med?.stats?.length) return null;
  return (
    <section
      className="relative bg-black text-white py-16 md:py-20 overflow-hidden"
      data-testid="med-results-section"
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full opacity-25"
          style={{ background: `radial-gradient(closest-side, ${RED}, transparent 70%)` }} />
      </div>
      <div className="relative max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
        <header className="max-w-[760px] mb-10">
          <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Expected Results</p>
          <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl">
            What The Numbers Say.
          </h2>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
          {med.stats.map((s, idx) => (
            <div key={idx}
              data-testid={`med-stat-${idx}`}
              className="rounded-2xl border bg-white/5 backdrop-blur px-6 py-7"
              style={{ borderColor: "rgba(255,255,255,0.10)" }}
            >
              <p className="font-heavy leading-none tracking-tight" style={{ color: RED, fontSize: "clamp(40px, 6vw, 64px)" }}>
                {s.v}
              </p>
              <p className="mt-3 text-[13px] tracking-[0.14em] font-extrabold uppercase text-white/65">{s.k}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────  SECTION — AVAILABLE STRENGTHS  ──────────────────────────── */
// Strength catalog per medication. Each strength card click opens the qualifier
// with the dose pre-attached (source: med_<slug>_<dose>) so the comms team can
// see exactly which dose drove the lead.
const STRENGTH_CATALOG = {
  tirzepatide:   { unit: "mg", values: ["2.5", "5", "7.5", "10", "12.5", "15"], defaultIdx: 1 },
  semaglutide:   { unit: "mg", values: ["0.25", "0.5", "1", "1.7", "2.4"],       defaultIdx: 2 },
  "oral-glp1":   { unit: "mg", values: ["3", "7", "14"],                          defaultIdx: 1 },
  "mic-b12":     { unit: "ml", values: ["0.5", "1", "2"],                         defaultIdx: 1 },
  "l-carnitine": { unit: "mg", values: ["250", "500", "1000"],                    defaultIdx: 1 },
  tadalafil:     { unit: "mg", values: ["2.5", "5", "10", "20"],                  defaultIdx: 1 },
  sildenafil:    { unit: "mg", values: ["25", "50", "100"],                       defaultIdx: 1 },
  testosterone:  { unit: "mg/wk", values: ["100", "150", "200"],                  defaultIdx: 1 },
  enclomiphene:  { unit: "mg", values: ["12.5", "25"],                            defaultIdx: 0 },
  "pt-141":      { unit: "mg", values: ["1", "1.75"],                             defaultIdx: 0 },
};

export function AvailableStrengths({ med, onPickStrength }) {
  const cfg = STRENGTH_CATALOG[med?.slug];
  const [hover, setHover] = useState(null);
  if (!cfg) return null;
  const defaultDose = cfg.values[cfg.defaultIdx];
  return (
    <section
      className="relative bg-white py-16 md:py-20 border-t border-black/5"
      data-testid="med-strengths-section"
      id="strengths"
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
        <header className="max-w-[760px] mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>
              Available Strengths
            </p>
            <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-4xl sm:text-5xl lg:text-6xl">
              Pick A Dose,<br /><span style={{ color: RED }}>Start Today.</span>
            </h2>
          </div>
          <p className="text-[13px] text-black/55 max-w-[280px]">
            Tap any strength to begin your guided assessment. A licensed provider sets the final dose.
          </p>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
          {cfg.values.map((dose, idx) => {
            const isDefault = dose === defaultDose;
            const isHover = hover === dose;
            return (
              <button
                key={dose}
                type="button"
                onMouseEnter={() => setHover(dose)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onPickStrength && onPickStrength(dose, cfg.unit)}
                data-testid={`med-strength-${med.slug}-${dose.replace(/\./g, "-")}`}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-white p-4 transition-all duration-300 hover:-translate-y-1 text-left"
                style={{
                  border: `1.5px solid ${isHover ? RED : "rgba(0,0,0,0.10)"}`,
                  boxShadow: isHover
                    ? "0 24px 64px -28px rgba(255,42,60,0.40)"
                    : "0 16px 50px -30px rgba(0,0,0,0.18), 0 1px 0 rgba(255,255,255,0.6) inset",
                }}
              >
                {/* Top accent line */}
                <div aria-hidden className="absolute top-0 left-4 right-4 h-px transition-opacity duration-300"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${RED}, transparent)`,
                    opacity: isHover ? 1 : 0,
                  }} />
                {isDefault && (
                  <span className="absolute top-3 right-3 text-[9px] tracking-[0.18em] font-extrabold uppercase px-1.5 py-0.5 rounded-full text-white"
                    style={{ background: RED }}>
                    Popular
                  </span>
                )}
                {/* Syringe + dose */}
                <Syringe size={16} className="text-black/35" />
                <div className="absolute inset-x-4 bottom-4">
                  <p className="font-heavy leading-none tracking-tight text-black" style={{ fontSize: "clamp(28px, 3.6vw, 42px)" }}>
                    {dose}
                    <span className="text-black/40 font-extrabold text-[14px] tracking-tight ml-1">{cfg.unit}</span>
                  </p>
                  <p className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-extrabold tracking-[0.14em] uppercase transition-colors"
                    style={{ color: isHover ? RED : "rgba(0,0,0,0.55)" }}>
                    {isHover ? "Begin assessment" : "Select dose"} <ChevronRight size={11} />
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Trust line + secondary CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-5 py-4 rounded-xl border bg-white"
          style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          <p className="text-[12.5px] text-black/65 leading-relaxed inline-flex items-center gap-2">
            <CheckCircle2 size={14} color={RED} />
            Provider-reviewed · US-licensed pharmacies · No insurance required.
          </p>
          <button
            type="button"
            onClick={() => onPickStrength && onPickStrength(defaultDose, cfg.unit)}
            data-testid={`med-strength-cta-${med.slug}`}
            className="inline-flex items-center gap-1 px-4 py-2 text-[11px] font-extrabold tracking-[0.14em] uppercase text-white"
            style={{ background: RED }}
          >
            Start With Most Popular Dose <ArrowUpRight size={12} />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────  RELATED CATEGORY LINKBACK (SEO)  ──────────────────────────── */
export function CategoryLinkback({ med }) {
  if (!med?.category) return null;
  const slug = med.category.toLowerCase().replace(/\s+/g, "-");
  return (
    <section className="bg-white py-10 border-t border-black/5">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-[10.5px] tracking-[0.22em] font-extrabold uppercase text-black/45">
            Part of the Medication Zone
          </p>
          <p className="font-heavy text-black text-[20px] sm:text-[22px] mt-1">
            Explore more {med.category} programs.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/category/${slug}`}
            data-testid="med-category-linkback"
            className="inline-flex items-center gap-1 px-4 py-2.5 text-[11px] font-extrabold tracking-[0.14em] uppercase text-white"
            style={{ background: "#000" }}>
            View {med.category} <ChevronRight size={11} />
          </Link>
          <Link to="/#medication-zone"
            data-testid="med-zone-linkback"
            className="inline-flex items-center gap-1 px-4 py-2.5 text-[11px] font-extrabold tracking-[0.14em] uppercase border"
            style={{ borderColor: "rgba(0,0,0,0.15)", color: "black", background: "white" }}>
            Back to Medication Zone <ArrowUpRight size={11} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* Provider hint (Sparkles + TrendingUp icons reserved for future expansion). */
export const __unused__ = { Sparkles, TrendingUp };
